// app.config.ts
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, PreloadAllModules, withInMemoryScrolling, withPreloading } from '@angular/router';
import { MatNativeDateModule } from '@angular/material/core';

import { provideStore } from '@ngrx/store';
const reducers = {}; // TODO: replace with real reducers

import { NgxEchartsModule } from 'ngx-echarts';

import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

// ⬇⬇ USE THE NEW UNIFIED ROUTES FILE
import { appRoutes } from 'app/app.routes';

import { provideAuth } from 'app/core/auth/auth.provider';
import { authInterceptor } from 'app/core/auth/auth.interceptor';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideFuse } from '@fuse';

import { dateTransportInterceptor } from './core/date/date-transport.interceptor';
import { eventHealthSaveInterceptor } from './core/services/CTEs/event-health-save.interceptor';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { provideReferenceDataDefaults } from './core/reference-data/reference-data.tokens';
import { DATE_UI_MODE, DateUIMode, makeDateUiProviders } from './core/date/date-ui.providers';

const DATE_MODE: DateUIMode = (localStorage.getItem('dateMode') as DateUIMode) || 'US';

export const appConfig: ApplicationConfig = {
  providers: [
    provideReferenceDataDefaults(),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true },
    },
    importProvidersFrom(MatNativeDateModule),
    { provide: DATE_UI_MODE, useValue: DATE_MODE },
    ...makeDateUiProviders(DATE_MODE),
    provideHttpClient(withInterceptors([authInterceptor, dateTransportInterceptor, eventHealthSaveInterceptor])),
    provideAnimations(),
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideStore(reducers),
    importProvidersFrom(
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts') as any,
      }),
    ),
    provideTransloco({
      config: {
        availableLangs: [
          { id: 'en', label: 'English' },
          { id: 'es', label: 'Español' },
          { id: 'gr', label: 'Ελληνικά' },
          { id: 'cn', label: '中文' },
          { id: 'bg', label: 'Български' },
        ],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      const defaultLang = translocoService.getDefaultLang();
      translocoService.setActiveLang(defaultLang);

      translocoService.load(defaultLang).subscribe({
        error: (err) => console.error('[Transloco init] failed', err),
      });

      return Promise.resolve();
    }),
    provideAuth(),
    provideIcons(),
    provideFuse({
      fuse: {
        layout: 'dense',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          { id: 'theme-default', name: 'Default' },
          { id: 'theme-brand', name: 'Brand' },
          { id: 'theme-teal', name: 'Teal' },
          { id: 'theme-rose', name: 'Rose' },
          { id: 'theme-purple', name: 'Purple' },
          { id: 'theme-amber', name: 'Amber' },
        ],
      },
    }),
  ],
};
